import React, { useMemo, useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody.tsx";
import { IColumn, IRow } from "@/domain/interfaces/sfTable.interface.ts";

import { applyFilters, cn } from "@/lib/utils.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange.tsx";
import { ILiquidation, useLiquidations } from "@/hooks/useLiquidations.ts";
import dayjs from "dayjs";
import { Loader } from "@/components/shared/loader.tsx";
import { PaneSubHeader } from "@/components/pane/paneSubHeader.tsx";
import { useDashboard } from "@/lib/store";
import { useFilterData } from "@/hooks/useFilterData";
import {
  EFilterType,
  ENumberComparisonType,
} from "@/domain/enums/dashboard.enum";

const columnHelper = createColumnHelper<ILiquidation>();

const columns = [
  columnHelper.accessor("symbol", {
    header: "Symbol",
    enableSorting: false,
  }),

  columnHelper.accessor("volume", {
    header: "Volume",
    cell: (info) => {
      const value = info.getValue();
      return (
        <HighlightedValueChange
          value={value}
          highlightNewTextColor={false}
          highlightPosNeg={false}
          className={cn(rowClassCb(info.row.original), "font-bold block")}
          shorten={true}
        />
      );
    },
    size: 50,
    minSize: 50,
  }),

  columnHelper.accessor("price", {
    header: "Price",
    cell: (row) => {
      const value = row.getValue();
      return <div>{value}</div>;
    },
    size: 50,
    minSize: 20,
  }),

  columnHelper.accessor("timestamp", {
    header: "Time",
    cell: (row) => {
      const value = row.getValue();
      return dayjs(value).utc().format("HH:mm:ss");
    },
    size: 120,
    minSize: 20,
  }),
];

function rowClassCb(row: IRow<ILiquidation>) {
  return row?.o?.S === "BUY" ? "bg-[#3CA64B]" : "bg-[#B2182C]";
}

export function LiquidationsScannerPaneBody() {
  const nodeId = usePaneContext();
  const { getNodeConfig, updateTabConfig } = useGrid(nodeId);
  const config = getNodeConfig();

  // Initialize default filters if they don't exist
  useEffect(() => {
    if (!config?.filters || !config?.filterValues) {
      updateTabConfig(
        {
          filters: [
            {
              type: EFilterType.number,
              title: "Vol",
              filterByField: "volume",
              defaultValue: 10000,
              numberComparisonType: ENumberComparisonType.mixed,
            },
          ],
          filterValues: [10000],
        },
        nodeId,
      );
    }
  }, [config, nodeId, updateTabConfig]);

  const volumeFilter = useMemo(() => {
    if (
      config?.filterValues &&
      Array.isArray(config.filterValues) &&
      config.filterValues.length > 0
    ) {
      return Number(config.filterValues[0]) || 10000;
    }
    return 10000;
  }, [config?.filterValues]);

  const { data: liquidations, isLoading } = useLiquidations(volumeFilter);
  const filteredData = useFilterData<ILiquidation>(liquidations || []);
  const { globalFilters, getGlobalFilterValues, blacklist } = useDashboard();

  const preparedData = useMemo(() => {
    if (!filteredData || isLoading) {
      return [];
    }

    let preparedData = applyFilters(
      filteredData,
      globalFilters,
      getGlobalFilterValues(),
    );

    if (blacklist.length > 0) {
      blacklist.forEach((symbol) => {
        preparedData = preparedData.filter((item) => item.symbol !== symbol);
      });
    }

    if (config?.filters && config?.filterValues) {
      return applyFilters<ILiquidation>(
        preparedData,
        config.filters,
        config.filterValues,
      );
    }

    return preparedData;
  }, [
    filteredData,
    isLoading,
    globalFilters,
    getGlobalFilterValues,
    blacklist,
    config.filters,
    config.filterValues,
  ]);

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <PaneSubHeader />
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={preparedData}
      />
    </>
  );
}
